/**
 * GestorConversorPage — Simulador real de remesas.
 *
 * Connected to the workflow + financial engine:
 *   • Rates loaded dynamically from /api/remittance/exchange-rates
 *   • Calls POST /api/remittance/simulate (with `db.remittance_simulations`)
 *   • Validates provider availability for the destination country
 *   • Bidirectional: gestor toggles "Yo pago" (forward) vs "Beneficiario recibe" (reverse)
 *   • History panel of recent simulations (does NOT touch the ledger)
 *   • CTA "Confirmar y enviar" → /remesas/enviar?sim={id} so the wizard
 *     prefills with the simulation snapshot, then THAT call (POST /operations)
 *     is what writes to the ledger via the existing remittance fund hook.
 *
 * UI policy: gestores never see the literal "USDT" — only "USD".
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight, Send, Loader2, AlertTriangle, ShieldCheck, Trash2, RotateCcw,
  ArrowRightLeft, Sparkles,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Only operational currencies are exposed. USDT is internal to the CEO and
// never appears in the gestor UI — see remittance_simulator.py for the
// 2-hop bridge that happens server-side.
const FUNDING_CCYS = [
  { key: 'UYU', label: 'UYU (Peso uruguayo)' },
  { key: 'USD', label: 'USD (Dólares)' },
];

const DEST_CCYS = [
  { key: 'CUP', label: 'CUP (Peso cubano)' },
  { key: 'MLC', label: 'MLC' },
  { key: 'USD', label: 'USD Clásica' },
];

const fmt = (n, max = 2) => Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: max });

const GestorConversorPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [direction, setDirection] = useState('forward'); // 'forward' = Yo pago | 'reverse' = Beneficiario recibe
  const [form, setForm] = useState({ amount: '1000', currency_from: 'UYU', currency_to: 'CUP' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/gestor/me/simulations?limit=8`, { headers: getAuthHeaders() });
      setHistory(r.data.items || []);
    } catch (e) {
      // History is a nice-to-have; surface in console so devs can investigate.
      console.warn('[conversor] simulations history fetch failed:', e?.message);
    } finally {
      setLoadingHistory(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const swap = () => {
    setDirection((d) => (d === 'forward' ? 'reverse' : 'forward'));
    setResult(null);
  };

  const simulate = async (e) => {
    e?.preventDefault?.();
    const amt = Number(form.amount);
    if (!amt || amt <= 0) {
      toast.error('Monto inválido');
      return;
    }
    if (form.currency_from === form.currency_to) {
      toast.error('Origen y destino no pueden ser iguales');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/remittance/simulate`, {
        direction,
        amount: amt,
        currency_from: form.currency_from,
        currency_to: form.currency_to,
      }, { headers: getAuthHeaders() });
      setResult(r.data);
      loadHistory();
    } catch (err) {
      setResult(null);
      toast.error(extractErrorMessage(err, 'No se pudo simular'));
    } finally {
      setBusy(false);
    }
  };

  const reuseSim = (sim) => {
    setDirection(sim.direction);
    setForm({
      amount: String(sim.input.amount),
      currency_from: sim.input.currency_from,
      currency_to: sim.input.currency_to,
    });
    setResult(sim);
  };

  const deleteSim = async (id) => {
    try {
      await axios.delete(`${API}/gestor/me/simulations/${id}`, { headers: getAuthHeaders() });
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success('Simulación eliminada');
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const confirmAndSend = () => {
    if (!result?.provider?.available) {
      toast.error('No hay proveedor disponible para esta moneda destino');
      return;
    }
    if (!result) return;
    navigate(`/remesas/enviar?sim=${result.id}`);
  };

  const dl = (ccy) => ccy;

  const breakdown = result?.breakdown;
  const providerOk = result?.provider?.available;
  const providerLabel = (() => {
    if (!result) return null;
    if (!result.provider) return null;
    if (result.provider.country == null) return 'Entrega directa (sin país intermediario)';
    if (result.provider.available) return `${result.provider.count} proveedor(es) disponible(s) en ${result.provider.country}`;
    return `Sin cobertura activa en ${result.provider.country}`;
  })();

  return (
    <div className="space-y-6" data-testid="gestor-conversor-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ArrowLeftRight className="w-6 h-6 text-amber-600" /> Simulador de remesas
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Calcula la conversión exacta con tasas en vivo. No afecta tu fondo hasta confirmar la remesa.
          </p>
        </div>
      </div>

      {/* Direction toggle */}
      <div className="flex items-center gap-2 text-xs">
        <button
          onClick={() => { setDirection('forward'); setResult(null); }}
          className={`px-3 py-1.5 rounded-full font-semibold transition-colors ${direction === 'forward' ? 'bg-amber-500 text-slate-900' : 'bg-slate-100 text-slate-600'}`}
          data-testid="dir-forward"
        >
          Yo pago →
        </button>
        <button
          onClick={() => { setDirection('reverse'); setResult(null); }}
          className={`px-3 py-1.5 rounded-full font-semibold transition-colors ${direction === 'reverse' ? 'bg-amber-500 text-slate-900' : 'bg-slate-100 text-slate-600'}`}
          data-testid="dir-reverse"
        >
          ← Beneficiario recibe
        </button>
      </div>

      <Card className="border-0 shadow-sm p-6">
        <form onSubmit={simulate} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">
              {direction === 'forward' ? 'Yo pago' : 'Beneficiario recibe'}
            </label>
            <div className="flex">
              <Input
                type="number" min="0" step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="rounded-r-none text-2xl font-mono h-14"
                data-testid="sim-amount-input"
              />
              <Select
                value={direction === 'forward' ? form.currency_from : form.currency_to}
                onValueChange={(v) => {
                  if (direction === 'forward') setForm({ ...form, currency_from: v });
                  else setForm({ ...form, currency_to: v });
                }}
              >
                <SelectTrigger className="rounded-l-none border-l-0 w-32 h-14" data-testid="sim-amount-ccy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(direction === 'forward' ? FUNDING_CCYS : [...FUNDING_CCYS, ...DEST_CCYS]).map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="md:col-span-1 flex justify-center md:pb-3">
            <button type="button" onClick={swap} className="p-2 rounded-full bg-slate-100 hover:bg-amber-100" title="Invertir dirección" data-testid="sim-swap-btn">
              <ArrowRightLeft className="w-4 h-4 text-slate-600" />
            </button>
          </div>

          <div className="md:col-span-4">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">
              {direction === 'forward' ? 'Beneficiario recibe en' : 'Yo pago en'}
            </label>
            <Select
              value={direction === 'forward' ? form.currency_to : form.currency_from}
              onValueChange={(v) => {
                if (direction === 'forward') setForm({ ...form, currency_to: v });
                else setForm({ ...form, currency_from: v });
              }}
            >
              <SelectTrigger className="h-14 text-base" data-testid="sim-target-ccy"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(direction === 'forward' ? DEST_CCYS : FUNDING_CCYS).map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-3">
            <Button type="submit" className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" disabled={busy} data-testid="sim-run-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Simular
            </Button>
          </div>
        </form>
      </Card>

      {/* Result */}
      {result && breakdown && (
        <Card className="border-0 shadow-sm overflow-hidden" data-testid="sim-result-card">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Resultado de la simulación
            </p>
            <div className="flex items-center gap-2">
              {providerOk ? (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid="sim-provider-ok">Proveedor disponible</Badge>
              ) : (
                <Badge variant="destructive" className="text-[10px]" data-testid="sim-provider-blocked">Sin proveedor</Badge>
              )}
              {result.rate_notes && (
                <span className="text-[10px] text-amber-700 italic" title={result.rate_notes}><AlertTriangle className="w-3 h-3 inline mr-1" />Placeholder</span>
              )}
            </div>
          </div>

          <div className="p-5 grid grid-cols-1 md:grid-cols-5 gap-3 text-sm">
            <div className="md:col-span-2 p-4 bg-slate-50 rounded space-y-1">
              <p className="text-[10px] uppercase text-slate-500">Yo pago</p>
              <p className="text-2xl font-bold font-mono text-slate-900" data-testid="sim-input-amount">
                {fmt(breakdown.input_amount, 4)} {dl(result.input.currency_from)}
              </p>
              <p className="text-[10px] text-slate-500 mt-2">Tasa congelada</p>
              <p className="text-sm font-mono">1 {dl(result.input.currency_from)} = {fmt(result.rate, 6)} {dl(result.input.currency_to)}</p>
            </div>
            <div className="md:col-span-3 grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded">
                <p className="text-[10px] uppercase text-slate-500">Bruto convertido</p>
                <p className="text-lg font-mono font-bold">{fmt(breakdown.gross_destination)} {dl(result.input.currency_to)}</p>
              </div>
              <div className="p-3 bg-rose-50 rounded">
                <p className="text-[10px] uppercase text-rose-600">Comisión Aircom</p>
                <p className="text-lg font-mono text-rose-700" data-testid="sim-commission">- {fmt(breakdown.commission_amount)} <span className="text-[10px]">({result.commission_pct}%)</span></p>
              </div>
              <div className="p-3 bg-amber-50 rounded">
                <p className="text-[10px] uppercase text-amber-700">Ganancia gestor</p>
                <p className="text-lg font-mono text-amber-700" data-testid="sim-profit">{fmt(breakdown.profit_amount)} <span className="text-[10px]">({result.profit_pct}%)</span></p>
              </div>
              <div className="p-3 bg-emerald-50 rounded">
                <p className="text-[10px] uppercase text-emerald-700">Beneficiario recibe</p>
                <p className="text-xl font-mono font-bold text-emerald-700" data-testid="sim-received">{fmt(breakdown.amount_received)} {dl(result.input.currency_to)}</p>
              </div>
            </div>
          </div>

          <div className="px-5 py-3 bg-slate-50 border-t flex items-center justify-between">
            <p className="text-[11px] text-slate-500">{providerLabel}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setResult(null)} data-testid="sim-clear-btn">
                <RotateCcw className="w-3 h-3 mr-1" /> Nueva simulación
              </Button>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                onClick={confirmAndSend}
                disabled={!providerOk}
                data-testid="sim-confirm-btn"
              >
                <Send className="w-3 h-3 mr-1" /> Confirmar y enviar
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* History */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="sim-history-card">
        <div className="px-4 py-3 border-b bg-slate-50">
          <p className="text-sm font-semibold">Historial de simulaciones</p>
          <p className="text-[10px] text-slate-400">Sólo registros locales. No afectan tu fondo.</p>
        </div>
        {loadingHistory ? (
          <p className="p-6 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> Cargando…</p>
        ) : history.length === 0 ? (
          <p className="p-6 text-center text-slate-400 text-sm">Aún no hay simulaciones.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b text-left text-[10px] text-slate-500 uppercase">
                <th className="p-2">Dir</th>
                <th className="p-2">Origen</th>
                <th className="p-2">Destino</th>
                <th className="p-2 text-right">Recibe</th>
                <th className="p-2">Proveedor</th>
                <th className="p-2">Fecha</th>
                <th className="p-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {history.map((s) => (
                <tr key={s.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="p-2">{s.direction === 'forward' ? '→' : '←'}</td>
                  <td className="p-2 font-mono">{fmt(s.breakdown.input_amount)} {dl(s.input.currency_from)}</td>
                  <td className="p-2 font-mono">{dl(s.input.currency_to)}</td>
                  <td className="p-2 text-right font-mono text-emerald-700">{fmt(s.breakdown.amount_received)}</td>
                  <td className="p-2">{s.provider?.available ? <span className="text-emerald-600">Disponible</span> : <span className="text-rose-600">Sin cobertura</span>}</td>
                  <td className="p-2 text-slate-400">{new Date(s.created_at).toLocaleString('es-ES')}</td>
                  <td className="p-2 text-right">
                    <button onClick={() => reuseSim(s)} className="p-1 text-slate-500 hover:text-amber-600" title="Reutilizar" data-testid={`sim-reuse-${s.id}`}>
                      <RotateCcw className="w-3 h-3" />
                    </button>
                    <button onClick={() => deleteSim(s.id)} className="p-1 text-slate-500 hover:text-rose-600" title="Eliminar" data-testid={`sim-delete-${s.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
};

export default GestorConversorPage;
